import { createSlice } from "@reduxjs/toolkit";

import { generateGuid } from "../../../common/utils/helpers";

const urgentAndForAssessmentDisabled = ["Miscellaneous", "Police"];

const recordsOrderingSlice = createSlice({
  name: "recordsOrdering",
  initialState: {
    // General
    projectData: null,
    recordsChecklistData: null,
    authsData: null,
    projectContactData: null,
    accidentData: null,
    projectWarnings: null, //missing auths, project info and contact info
    //Contacts
    rawContactsData: null, //Raw contacts data fetched from API
    normalizedContactsData: null, //Contacts data with common field names (Used by undertakings view)
    linkedContactsData: null, //Contacts linked with records checklist (Used by contacts view)
    recordsCart: [],
    recordsQuickOrder: [],
    currentRecordId: null,
    contactDetailsDrawerData: null,
    //Undertakings
    undertakingsData: null,
    undertakingsCart: [],
    undertakingsQuickOrder: [],
    undertakingDetailsDrawerData: null,
    tablesState: {
      contacts: { columns: null, filters: null },
      orders: { columns: null, filters: null },
      undertakings: { columns: null, filters: null },
    },
  },
  reducers: {
    /*********
    / GENERAL
    *********/
    setProjectData(state, action) {
      state.projectData = action.payload;
    },

    setRecordsChecklistData(state, action) {
      state.recordsChecklistData = action.payload;
    },

    setAuthsData(state, action) {
      state.authsData = action.payload;
    },

    setProjectContactData(state, action) {
      state.projectContactData = action.payload;
    },

    setAccidentData(state, action) {
      state.accidentData = action.payload;
    },

    updateProjectWarnings(state, action) {
      state.projectWarnings = action.payload;
    },

    updateTablesState(state, action) {
      state.tablesState[action.payload.table][action.payload.type] = action.payload.updatedState;
    },

    /********
    Contacts
    ********/
    setRawContactsData(state, action) {
      state.rawContactsData = action.payload;
    },

    setNormalizedContactsData(state, action) {
      state.normalizedContactsData = action.payload;
    },

    setLinkedContactsData(state, action) {
      state.linkedContactsData = action.payload;
    },

    addOrderRecord(state, action) {
      const newOrderRecord = {
        ...action.payload,
        recordId: generateGuid(),
        orderStatus: { success: null, errorMessage: "" },
      };
      state.recordsCart = [...state.recordsCart, newOrderRecord];
    },

    addQuickOrderRecord(state, action) {
      const newOrderRecord = {
        ...action.payload,
        recordId: generateGuid(),
        orderStatus: { success: null, errorMessage: "" },
      };
      state.recordsQuickOrder = [...state.recordsQuickOrder, newOrderRecord];
    },

    duplicateRecord(state, action) {
      const isQuickOrder = state.recordsQuickOrder.length > 0;
      const records = isQuickOrder ? state.recordsQuickOrder : state.recordsCart;
      const currentRecord = action.payload;
      const newRecord = { ...currentRecord, recordId: generateGuid(), recordType: "" };
      const currentRecordIndex = records.findIndex((o) => o.recordId === currentRecord.recordId);
      const updatedRecords = records.toSpliced(currentRecordIndex + 1, 0, newRecord);

      if (isQuickOrder) {
        state.recordsQuickOrder = updatedRecords;
      } else {
        state.recordsCart = updatedRecords;
      }
    },

    //Remove a single record from the order
    removeOrderRecord(state, action) {
      const isQuickOrder = state.recordsQuickOrder.length > 0;
      const records = isQuickOrder ? state.recordsQuickOrder : state.recordsCart;
      const recordId = action.payload;
      const updatedRecords = records.filter((ogOrderRecord) => ogOrderRecord.recordId !== recordId);

      if (isQuickOrder) {
        state.recordsQuickOrder = updatedRecords;
      } else {
        state.recordsCart = updatedRecords;
      }
    },

    //Remove all records from the order
    removeOrder(state, action) {
      const isQuickOrder = state.recordsQuickOrder.length > 0;
      const records = isQuickOrder ? state.recordsQuickOrder : state.recordsCart;
      const updatedRecord = action.payload;

      const updatedRecords = records.filter(
        (ogOrderRecord) => ogOrderRecord.orderingCollectionItemId !== updatedRecord.orderingCollectionItemId
      );

      if (isQuickOrder) {
        state.recordsQuickOrder = updatedRecords;
      } else {
        state.recordsCart = updatedRecords;
      }
    },

    removeAllOrders(state) {
      if (state.recordsQuickOrder.length > 0) {
        state.recordsQuickOrder = [];
      } else {
        state.recordsCart = [];
      }
    },

    updateOrderRecord(state, action) {
      const updatedOrderId = action.payload.id;
      const updates = action.payload.updates;
      const isQuickOrder = state.recordsQuickOrder.length > 0;
      const records = isQuickOrder ? state.recordsQuickOrder : state.recordsCart;
      const originalOrder = records.find((record) => record.recordId === updatedOrderId);
      const updatedOrder = { ...JSON.parse(JSON.stringify(originalOrder)), ...updates };

      const updatedRecords = records.map((ogOrderRecord) => {
        return ogOrderRecord.recordId === updatedOrderId ? updatedOrder : ogOrderRecord;
      });

      if (isQuickOrder) {
        state.recordsQuickOrder = updatedRecords;
      } else {
        state.recordsCart = updatedRecords;
      }
    },

    nextOrderRecord(state, action) {
      const isQuickOrder = state.recordsQuickOrder.length > 0;
      const records = isQuickOrder ? state.recordsQuickOrder : state.recordsCart;
      const previousRecordId = action.payload;
      const previousRecordIndex = records.findIndex((o) => o.recordId === previousRecordId);
      const nextRecordId = records[previousRecordIndex + 1]?.recordId;

      if (nextRecordId) {
        state.currentRecordId = nextRecordId;
      } else {
        state.currentRecordId = null;
      }
    },

    markAllForAssessment(state) {
      const isQuickOrder = state.recordsQuickOrder.length > 0;
      const records = isQuickOrder ? state.recordsQuickOrder : state.recordsCart;

      const updatedRecords = records.map((record) =>
        !urgentAndForAssessmentDisabled.includes(record.correspondentType) ? { ...record, forAssessment: true } : record
      );

      if (isQuickOrder) {
        state.recordsQuickOrder = updatedRecords;
      } else {
        state.recordsCart = updatedRecords;
      }
    },

    markAllAsUrgent(state) {
      const isQuickOrder = state.recordsQuickOrder.length > 0;
      const records = isQuickOrder ? state.recordsQuickOrder : state.recordsCart;

      const updatedRecords = records.map((record) =>
        !urgentAndForAssessmentDisabled.includes(record.correspondentType) && !record.orderThroughMedchart
          ? { ...record, urgent: true }
          : record
      );

      if (isQuickOrder) {
        state.recordsQuickOrder = updatedRecords;
      } else {
        state.recordsCart = updatedRecords;
      }
    },

    // Drawer is open when there is data to display
    setContactDetailsDrawerData(state, action) {
      state.contactDetailsDrawerData = action.payload;
    },

    closeContactDetailsDrawer(state) {
      state.contactDetailsDrawerData = null;
    },

    /************
    Undertakings
    ************/
    setUndertakingsData(state, action) {
      state.undertakingsData = action.payload;
    },

    setUndertakingDetailsDrawerData(state, action) {
      console.log(action.payload);
      state.undertakingDetailsDrawerData = action.payload;
    },

    closeUndertakingDetailsDrawer(state) {
      state.undertakingDetailsDrawerData = null;
    },

    addUndertakingToCart(state, action) {
      state.undertakingsCart = [...state.undertakingsCart, action.payload];
    },

    removeUndertakingFromCart(state, action) {
      const undertakingToRemove = action.payload;

      const updatedUndertakingsCart = state.undertakingsCart.filter(
        (ogUndertaking) => ogUndertaking.collectionItemId !== undertakingToRemove.collectionItemId
      );

      state.undertakingsCart = updatedUndertakingsCart;
    },

    removeAllUndertakingsFromCart(state) {
      state.undertakingsCart = [];
    },

    setUndertakingsQuickOrder(state, action) {
      if (action.payload) {
        state.undertakingsQuickOrder = [action.payload];
      } else {
        state.undertakingsQuickOrder = [];
      }
    },
  },
});

export const recordsOrderingActions = recordsOrderingSlice.actions;

export default recordsOrderingSlice.reducer;
